/**
 * Web persistence: two named idb-keyval stores (one database each, since
 * idb-keyval opens one object store per database). State records are keyed
 * by section name; history records by sequence number, one per game.
 */
import {
  clear,
  createStore,
  delMany,
  entries,
  setMany,
  type UseStore,
} from "idb-keyval";

import type { HistoryKey, StorageAdapter } from "./types";

export const WEB_STATE_DB = "synapse-state";
export const WEB_HISTORY_DB = "synapse-history";

/**
 * A lost IndexedDB connection: Safari's "Connection to Indexed Database
 * server lost", a closing/closed database, or an InvalidStateError (also
 * what Firefox private mode before v115 throws at open). idb-keyval caches
 * its connection per store, so these never heal without a new store.
 */
export function isLostConnection(error: unknown): boolean {
  const e = error as { name?: unknown; message?: unknown } | null;
  if (e?.name === "InvalidStateError") return true;
  const message = typeof e?.message === "string" ? e.message : "";
  return /connection to indexed database server lost|database connection is closing|database is (closed|closing)/i.test(
    message,
  );
}

/** `makeStore` is idb-keyval's createStore; tests inject failing stores. */
export function createWebAdapter(
  makeStore: (db: string, store: string) => UseStore = createStore,
): StorageAdapter {
  let state: UseStore;
  let history: UseStore;
  const connect = () => {
    state = makeStore(WEB_STATE_DB, "state");
    history = makeStore(WEB_HISTORY_DB, "history");
  };
  connect();

  /** Run an operation; on a lost connection, reconnect and retry once. */
  const run = async <T>(op: () => Promise<T>): Promise<T> => {
    try {
      return await op();
    } catch (error) {
      if (!isLostConnection(error)) throw error;
      console.warn("⚠️ IndexedDB connection lost; reconnecting:", error);
      connect();
      return op();
    }
  };

  return {
    readState: () =>
      run(async () => new Map((await entries(state)) as [string, unknown][])),
    writeState: (records) => run(() => setMany(records, state)),
    deleteState: (names) => run(() => delMany(names, state)),
    readHistory: () =>
      run(async () =>
        ((await entries(history)) as [unknown, unknown][])
          .filter((r): r is [HistoryKey, unknown] => typeof r[0] === "number")
          .sort((a, b) => a[0] - b[0]),
      ),
    writeHistory: (records) => run(() => setMany(records, history)),
    deleteHistory: (keys) => run(() => delMany(keys, history)),
    clear: () =>
      run(async () => {
        await clear(history);
        await clear(state);
      }),
  };
}

let persistRequested = false;

/**
 * Ask the browser not to evict our storage under pressure. Best effort,
 * once per session; browsers may refuse or not support it.
 */
export function requestPersistentStorage(): void {
  if (persistRequested) return;
  persistRequested = true;
  try {
    const storage = (globalThis as { navigator?: Navigator }).navigator
      ?.storage;
    storage?.persist?.().catch(() => undefined);
  } catch {
    // unsupported: storage stays best-effort
  }
}
